import React, { useMemo, useState, useEffect, useCallback } from 'react';
import PlayerPresenceRow from './PlayerPresenceRow';

const STATUS_ORDER = { online: 0, idle: 1, offline: 2 };

function PlayerPresenceList({ players = {}, onPlayerClick }) {
  const [focusedIndex, setFocusedIndex] = useState(-1);

  const rows = useMemo(() => {
    const vals = Object.values(players || {});
    return vals
      .filter((p) => {
        if (p.status === 'offline') {
          const fortyFiveMinutes = 45 * 60 * 1000;
          if (!p.lastUpdatedAt) return false;
          return Date.now() - p.lastUpdatedAt <= fortyFiveMinutes;
        }
        return true;
      })
      .sort((a, b) => {
        const sa = STATUS_ORDER[a.status] ?? 3;
        const sb = STATUS_ORDER[b.status] ?? 3;
        if (sa !== sb) return sa - sb;
        return String(a.playerId || '').localeCompare(String(b.playerId || ''));
      });
  }, [players]);

  const handleKeyDown = useCallback(
    (e) => {
      if (!rows.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setFocusedIndex((idx) => Math.min(rows.length - 1, idx + 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setFocusedIndex((idx) => Math.max(0, idx - 1));
      } else if (e.key === 'Enter' && focusedIndex >= 0) {
        const p = rows[focusedIndex];
        if (p) onPlayerClick?.(p.playerId);
      }
    },
    [focusedIndex, onPlayerClick, rows],
  );

  useEffect(() => {
    const handler = (e) => handleKeyDown(e);
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [handleKeyDown]);

  return (
    <div className="card">
      <div className="card-body p-0">
        {rows.map((p, idx) => (
          <PlayerPresenceRow
            key={p.playerId}
            player={p}
            focused={idx === focusedIndex}
            onClick={onPlayerClick}
            onMouseEnter={() => setFocusedIndex(idx)}
            onMouseLeave={() => setFocusedIndex(-1)}
          />
        ))}
        {!rows.length && (
          <div className="p-3 text-muted">No players to display.</div>
        )}
      </div>
    </div>
  );
}

export default PlayerPresenceList;
